import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FiLock, FiArrowRight } from "react-icons/fi";
import { ApiHandler } from "../../helper/ApiHandler";
import illustration from "../../assest/Reset-password-amico.png";

const RESET_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .rp-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .rp-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes rp-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .rp-float { animation: rp-float 4.5s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .rp-float { animation: none !important; }
  }
`;

const ResetPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { token } = useParams();
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            setError("Please fill in both password fields.");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setError(""); // clear previous error
        if (isSubmitting) return;
        setIsSubmitting(true);

        const body = {
            token: token,
            newPassword: password,
            confirmPassword: confirmPassword
        };

        try {
            const result = await ApiHandler(
                "/new_password.php",
                "POST",
                body,
                undefined,
                dispatch,
                navigate
            );

            if (result?.data?.status === "1") {
                toast.success(result.data.msg || "Password reset successfully.");
                navigate("/");
            } else {
                toast.error(result?.data?.msg || "Failed to reset password. Please try again.");
                setPassword("");
                setConfirmPassword("");
            }
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <style>{RESET_STYLES}</style>

            <div
                className="rp-body min-h-screen flex items-center justify-center px-4 py-8"
                style={{
                    background:
                        "radial-gradient(90% 70% at 15% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 100% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
                }}
            >
                <div
                    className="max-w-6xl w-full bg-white shadow-[0_35px_90px_-28px_rgba(28,20,16,0.3)] overflow-hidden grid grid-cols-1 md:grid-cols-2 border-2 border-[#FDE8CD] relative"
                    style={{ borderRadius: "1rem 3rem 1rem 3rem" }}
                >
                    {/* accent corner ribbon */}
                    <div
                        aria-hidden="true"
                        className="hidden md:block absolute top-0 right-0 w-24 h-24 pointer-events-none"
                        style={{
                            background: "linear-gradient(135deg, transparent 50%, #FDE8CD 50%)",
                            borderRadius: "0 1rem 0 0",
                        }}
                    />

                    {/* Left: Illustration */}
                    <div
                        className="hidden md:flex items-center justify-center p-6 relative overflow-hidden"
                        style={{
                            background:
                                "radial-gradient(120% 100% at 30% 20%, #FEF3E5 0%, #FFF4E5 60%, #FFFBF5 100%)",
                        }}
                    >
                        <div
                            aria-hidden="true"
                            className="absolute -top-10 -left-10 w-56 h-56 rounded-full opacity-30 blur-3xl"
                            style={{ background: "radial-gradient(circle, #F59E0B, transparent 70%)" }}
                        />
                        <div
                            aria-hidden="true"
                            className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full opacity-25 blur-3xl"
                            style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
                        />
                        <img
                            src={illustration}
                            alt="Reset Password Illustration"
                            className="rp-float max-h-[400px] w-auto relative z-10"
                        />
                    </div>

                    {/* Right: Form */}
                    <div className="p-8 sm:p-12">
                        <h2
                            className="text-3xl font-bold text-center text-[#1C1410]"
                            style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
                        >
                            Reset{" "}
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]">
                                Password
                            </span>
                        </h2>
                        <p className="text-center text-gray-500 mb-6"></p>

                        <form onSubmit={handleSubmit}>
                            <div className="mb-5">
                                <label htmlFor="password" className="block mb-1 text-sm font-medium text-[#3D2E22]">
                                    New Password
                                </label>
                                <div className="relative">
                                    <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                                    <input
                                        type="password"
                                        name="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="At least 6 characters"
                                        required
                                        minLength={6}
                                        className="w-full h-12 pl-11 pr-4 bg-white border border-[#F0DCC8] rounded-xl text-sm focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-all"
                                    />
                                </div>
                            </div>

                            <div className="mb-5">
                                <label htmlFor="confirmPassword" className="block mb-1 text-sm font-medium text-[#3D2E22]">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Re-enter your new password"
                                        required
                                        minLength={6}
                                        className="w-full h-12 pl-11 pr-4 bg-white border border-[#F0DCC8] rounded-xl text-sm focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-all"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="text-rose-500 text-sm mb-4 text-center font-medium">{error}</div>
                            )}

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white py-3 rounded-xl text-sm font-bold shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 disabled:opacity-70"
                            >
                                {isSubmitting ? "Resetting..." : "Reset Password"}
                                {!isSubmitting && (
                                    <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;